import React, { useState } from 'react';
import { Button, Input, Popconfirm, Space, Table, TableColumnsType, Tag } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { getModelConfigList, ModelConfigVo, toggleModelConfig, deleteModelConfig, PROVIDER_OPTIONS } from '@/api/modelConfig';
import { useUpdate } from '@/common/hook';
import EditModelConfig from './editModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const columns: TableColumnsType<ModelConfigVo> = [
  { title: '配置名称', dataIndex: 'configName', key: 'configName', width: 150 },
  {
    title: '供应商',
    dataIndex: 'provider',
    key: 'provider',
    width: 100,
    render: (text: string) => PROVIDER_OPTIONS.find(p => p.value === text)?.label || text,
  },
  { title: '模型名称', dataIndex: 'modelName', key: 'modelName', width: 150 },
  { title: 'API Key 引用', dataIndex: 'apiKeyRef', key: 'apiKeyRef', ellipsis: true },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    width: 80,
    render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
  },
  {
    title: '创建人',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const ModelConfigList = () => {
  const [editData, setEditData] = useState<ModelConfigVo>();
  const [visible, setVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [update, updateFn] = useUpdate();

  const { data: list, loading } = useRequest(() => getModelConfigList({ keyword }), { refreshDeps: [update, keyword] });

  const changeStatus = (record: ModelConfigVo) => {
    toggleModelConfig({ id: record.id, status: record.status === 1 ? 0 : 1 }).then(updateFn);
  };

  const onDelete = (id: number) => {
    deleteModelConfig(id).then(updateFn);
  };

  const buildColumns = [
    ...columns,
    {
      title: '操作',
      key: 'operation',
      width: 150,
      render: (_: any, record: ModelConfigVo) => (
        <Space size={8}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setVisible(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => changeStatus(record)}>
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm title="确定删除该配置？" onConfirm={() => onDelete(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
          新增配置
        </Button>
        <Input
          placeholder="搜索配置名称/模型名称"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<ModelConfigVo> columns={buildColumns} dataSource={list ?? []} loading={loading} rowKey="id" />
      {visible && (
        <EditModelConfig
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setVisible(false);
            setEditData(undefined);
          }}
        />
      )}
    </>
  );
};

export default ModelConfigList;